// Cognito Hosted UI, authorization code flow with PKCE (research.md D-07).
//
// The Hosted UI means no password field is ever rendered or handled by this
// code, which is what Principle II requires. There is no sign-up affordance:
// accounts are administrator-created (FR-003a).

import { config } from '../config.js';

const TOKEN_KEY = 'tf.tokens';
const VERIFIER_KEY = 'tf.pkce_verifier';

interface StoredTokens {
  accessToken: string;
  refreshToken?: string;
  expiresAt: number;
}

function base64UrlEncode(bytes: Uint8Array): string {
  return btoa(String.fromCharCode(...bytes))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '');
}

async function createPkcePair(): Promise<{ verifier: string; challenge: string }> {
  const verifier = base64UrlEncode(crypto.getRandomValues(new Uint8Array(64)));
  const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(verifier));
  return { verifier, challenge: base64UrlEncode(new Uint8Array(digest)) };
}

function readTokens(): StoredTokens | undefined {
  const raw = sessionStorage.getItem(TOKEN_KEY);
  if (!raw) return undefined;
  try {
    return JSON.parse(raw) as StoredTokens;
  } catch {
    return undefined;
  }
}

function writeTokens(tokens: StoredTokens): void {
  // sessionStorage rather than localStorage: tokens die with the tab, which
  // limits the window in which a shared machine exposes an account.
  sessionStorage.setItem(TOKEN_KEY, JSON.stringify(tokens));
}

export function clearTokens(): void {
  sessionStorage.removeItem(TOKEN_KEY);
  sessionStorage.removeItem(VERIFIER_KEY);
}

export function getAccessToken(): string | undefined {
  const tokens = readTokens();
  if (!tokens) return undefined;
  if (Date.now() >= tokens.expiresAt) return undefined;
  return tokens.accessToken;
}

export function isSignedIn(): boolean {
  return getAccessToken() !== undefined;
}

export async function signIn(): Promise<void> {
  const { verifier, challenge } = await createPkcePair();
  sessionStorage.setItem(VERIFIER_KEY, verifier);

  const params = new URLSearchParams({
    client_id: config.cognitoClientId,
    response_type: 'code',
    scope: 'email openid profile',
    redirect_uri: config.redirectUri,
    code_challenge: challenge,
    code_challenge_method: 'S256',
  });

  window.location.assign(`https://${config.cognitoDomain}/oauth2/authorize?${params.toString()}`);
}

export function signOut(): void {
  clearTokens();
  const params = new URLSearchParams({
    client_id: config.cognitoClientId,
    logout_uri: window.location.origin + '/',
  });
  window.location.assign(`https://${config.cognitoDomain}/logout?${params.toString()}`);
}

/** Exchange the authorization code for tokens. Called on /auth/callback. */
export async function completeSignIn(code: string): Promise<void> {
  const verifier = sessionStorage.getItem(VERIFIER_KEY);
  if (!verifier) throw new Error('Sign-in could not be completed. Please try again.');

  const response = await fetch(`https://${config.cognitoDomain}/oauth2/token`, {
    method: 'POST',
    headers: { 'content-type': 'application/x-www-form-urlencoded' },
    body: new URLSearchParams({
      grant_type: 'authorization_code',
      client_id: config.cognitoClientId,
      code,
      redirect_uri: config.redirectUri,
      code_verifier: verifier,
    }),
  });

  if (!response.ok) throw new Error('Sign-in failed. Please try again.');

  const payload = (await response.json()) as {
    access_token: string;
    refresh_token?: string;
    expires_in: number;
  };

  sessionStorage.removeItem(VERIFIER_KEY);
  writeTokens({
    accessToken: payload.access_token,
    ...(payload.refresh_token ? { refreshToken: payload.refresh_token } : {}),
    // 30s of slack so a request started just under the wire does not arrive expired.
    expiresAt: Date.now() + (payload.expires_in - 30) * 1000,
  });
}
